
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userAssignmentService } from '@/services/userAssignmentService';
import { toast } from 'sonner';

export const useUnassignedUsers = () => {
  return useQuery({
    queryKey: ['unassignedUsers'],
    queryFn: () => userAssignmentService.getUnassignedUsers(),
  });
};

export const useActiveAffiliates = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['activeAffiliates'],
    queryFn: () => userAssignmentService.getActiveAffiliates(),
    enabled: options?.enabled ?? true, // Default to true for backward compatibility
  });
};

export const useAssignUserToAffiliate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      userId, 
      affiliateId, 
      referralCode 
    }: { 
      userId: string; 
      affiliateId: string; 
      referralCode: string; 
    }) => userAssignmentService.assignUserToAffiliate(userId, affiliateId, referralCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unassignedUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminReferrals'] });
      toast.success('User assigned successfully');
    },
    onError: () => {
      toast.error('Failed to assign user');
    },
  });
};

export const useTransferUserToAffiliate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      userId, 
      newAffiliateId, 
      newReferralCode 
    }: { 
      userId: string; 
      newAffiliateId: string; 
      newReferralCode: string; 
    }) => userAssignmentService.transferUserToAffiliate(userId, newAffiliateId, newReferralCode),
    onMutate: async ({ userId, newAffiliateId }) => {
      // Optimistic update - immediately update the UI
      console.log('🔄 Starting optimistic update for transfer...');
      
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['adminReferrals'] });
      
      // Get current data
      const currentData = queryClient.getQueriesData({ queryKey: ['adminReferrals'] });
      
      // Optimistically update the cache
      currentData.forEach(([queryKey, data]: any) => {
        if (data?.data) {
          const updatedData = {
            ...data,
            data: data.data.map((referral: any) => {
              if (referral.referred_user_id === userId) {
                return {
                  ...referral,
                  affiliateName: 'Transferring...' // Temporary state
                };
              }
              return referral;
            })
          };
          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      return { currentData };
    },
    onSuccess: () => {
      console.log('🔄 Transfer completed successfully');
      
      // Invalidate and refetch with reduced scope
      queryClient.invalidateQueries({ 
        queryKey: ['adminReferrals'],
        refetchType: 'active' // Only refetch active queries
      });
      
      toast.success('User transferred successfully');
    },
    onError: (error, variables, context) => {
      console.error('❌ Transfer mutation failed:', error);
      
      // Revert optimistic update
      if (context?.currentData) {
        context.currentData.forEach(([queryKey, data]: any) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      toast.error('Failed to transfer user');
    },
  });
};
